import SEO from "../components/SEO"
import React, { useEffect } from "react"
import GlobalStyle from "../components/GlobalStyle"
import Nav from "../components/Nav"
import styled from "styled-components"
import AppStoreLinks from "../components/AppStoreLinks"
import Analytics from "../utils/tracking"
import stockperksLogo from "../images/stockperks-logo.png"
import etoroLogo from "../images/clients/eToro/eToro-logo.svg"
import eToroBanner from "../images/clients/eToro/eToro-banner.png"
import { windowSize } from "../utils/responsive"

const EtoroPage = () => {
    useEffect(() => {
        ;(async () => {
            await Analytics.track("eToro x Stockperks Landing Page")
        })()
    }, [])

    const perksMatrix = {
        columns: [
            { key: "starter", label: "Starter" },
            { key: "engaged", label: "Engaged" },
            { key: "advanced", label: "Advanced" },
            { key: "insider", label: "Insider" },
            { key: "allAccess", label: "All-Access" },
        ],
        rows: [
            {
                label: "# eToro Shares",
                type: "range",
                values: {
                    starter: "Up to 99",
                    engaged: "Up to 499",
                    advanced: "Up to 999",
                    insider: "Over 999",
                    allAccess: "Over 1999",
                },
            },
            {
                label: "Welcome Video",
                type: "check",
                values: { starter: true, engaged: true, advanced: true, insider: true, allAccess: true },
            },
            {
                label: "Bi-Annual Q&A with IRO",
                type: "check",
                values: { starter: true, engaged: true, advanced: true, insider: true, allAccess: true },
            },
            {
                label: "Delta PRO+ Investment Tracker",
                type: "check",
                values: { starter: false, engaged: true, advanced: true, insider: true, allAccess: true },
            },
            {
                label: "Exclusive Webinars",
                type: "check",
                values: { starter: false, engaged: false, advanced: true, insider: true, allAccess: true },
            },
            {
                label: "$50 Gift Card",
                type: "check",
                values: { starter: false, engaged: false, advanced: true, insider: true, allAccess: true },
            },
            {
                label: "eToro Welcome Pack",
                type: "check",
                values: { starter: false, engaged: false, advanced: false, insider: true, allAccess: true },
            },
            {
                label: "Raffle To Attend Car Race",
                type: "check",
                values: { starter: false, engaged: false, advanced: false, insider: true, allAccess: true },
            },
            {
                label: "Invitation-only Event",
                type: "check",
                values: { starter: false, engaged: false, advanced: false, insider: false, allAccess: true },
            },


        ],
    }

    const faqs = [
        {
            q: "What do I need to qualify for the eToro Future Shareholder Reward Program?",
            a: "You need at least 1 ETOR share to qualify for eToro shareholder rewards.\nYou must provide proof of your shareholding via Stockperks.",
        },
        {
            q: "How do I access Stockperks?",
            a: (
                <>
                    The Stockperks app is available in the Apple App and Google Play Stores globally.
                    {"\n"}• Download the app via the links below, or search for “Stockperks” in your app store
                    {"\n"}• Once downloaded, create a Stockperks user profile
                    {"\n"}• Then search for “eToro” in the app
                    {"\n"}• Click the blue “Validate your portfolio” button and you will be guided through the process of providing your ETOR stock ownership details
                    {"\n"}• Once confirmed you can then redeem the perk that corresponds to your level of ETOR stock ownership
                </>
            ),
        },
        {
            q: "Do I need to hold my ETOR shares in an eToro account?",
            a: "No. You can hold your ETOR shares with any brokerage globally to participate in the eToro Shareholder Reward Program.",
        },
        {
            q: "How does the validation process work?",
            a: "eToro has partnered with Stockperks, the largest global shareholder perks marketplace, to validate and distribute shareholder rewards.\n\nThe most secure and efficient way to validate your shares is through the automated validation process in the Stockperks app, which connects to your online brokerage account directly. Your ETOR stock position is usually validated instantaneously.\n\nIf your brokerage is not supported by the automated process, you can upload an image of your proof of ownership for manual validation. Your shares will be validated usually within 1–3 business days depending on volumes.",
        },
        {
            q: "What technology powers the automated validation?",
            a: "The automated validation capabilities are provided by either Plaid or SnapTrade. Both are industry-standard technologies that connect thousands of financial institutions and apps using end-to-end encryption.",
        },
        {
            q: "Can Stockperks or eToro see my login details or trading activity?",
            a: "No. Your login details are masked and encrypted so that neither Stockperks nor eToro can see them. We only request access to your portfolio to validate your eligibility for the shareholder perks.",
        },
        {
            q: "Will my personal information be sold or shared?",
            a: "Absolutely not. We do not sell or share any of your information with third parties.",
        },
        {
            q: "Why did eToro choose to partner with Stockperks?",
            a: "Stockperks is the largest and most established shareholder rewards marketplace globally. As importantly, Stockperks takes its users’ privacy and data very seriously. eToro wouldn’t have partnered with them if it weren’t confident in their security standards and commitment to protecting your information.",
        },
        {
            q: "Where can I learn more about data security practices?",
            a: (
                <>
                    You can review the security and privacy policies of each of the partners:
                    {"\n"}
                    <a href="https://plaid.com/safety/" target="_blank" rel="noopener noreferrer">
                        Plaid
                    </a>
                    {" | "}
                    <a href="https://snaptrade.com/security" target="_blank" rel="noopener noreferrer">
                        SnapTrade
                    </a>
                    {" | "}
                    <a href="https://stockperks.com/privacy" target="_blank" rel="noopener noreferrer">
                        Stockperks
                    </a>
                </>
            ),
        },
        {
            q: "What if my brokerage is not supported by Plaid / SnapTrade?",
            a: "You can upload an image of your recent proof of ownership in the Stockperks app by selecting “Upload Manual Statement” at the bottom of the “Brokerage connection” page in the Stockperks app or via the desktop version of Stockperks (when available) and follow the instructions.",
        },
        {
            q: "What is a valid proof of ownership?",
            a: (
                <>
                    If you upload an image of your proof of ownership it must be from{" "}
                    <strong>within the last 3 months</strong> and show:
                    {"\n"}• Your name
                    {"\n"}• The number of ETOR shares you own
                    {"\n"}• The date of the document
                    {"\n"}• The brokerage name
                    {"\n\n"}You can redact any other information you don’t want to share.
                </>
            ),
        },
        {
            q: "What if I’m still not comfortable connecting my brokerage via Plaid / SnapTrade?",
            a: "You can upload an image of your recent proof of ownership in the Stockperks app by selecting “Upload Manual Statement” at the bottom of the “Brokerage connection” page in the Stockperks app.\n\nPlease note that the automated validation process is instantaneous, while the manual option may take 1–3 business days depending on processing queue volumes.",
        },
        {
            q: "What types of rewards are available for eToro shareholders?",
            a: "A full list of available rewards is provided in the Stockperks app on the eToro page.\n\nOnce your shares are validated, select the perk that applies to you from the list and then press the “Redeem Perk” button.\n\nIf you have automatically validated your shares, you will immediately be able to redeem your perk(s) in the Stockperks app.\n\nIf you uploaded your proof of ownership for manual validation, Stockperks will email you when your shares have been validated, at which point you can go into the Stockperks app to redeem your perks.",
        },
    ]

    return (
        <>
            <SEO title="Stockperks x eToro" url={"/etoro"} />
            <GlobalStyle />
            <Nav siteTitle={"Stockperks x eToro"} />

            <PageContent>
                <Header>
                    <TopLogo src={etoroLogo} alt="eToro" $variant="etoro" />
                    <Divider />
                    <a href={"https://www.stockperks.com"} aria-label="Stockperks website">
                        <TopLogo style={{ marginTop: 18 }} src={stockperksLogo} alt="Stockperks" />
                    </a>
                </Header>

                <Body>
                    <Content>
                        <BannerImage src={eToroBanner} alt="eToro Shareholder Reward Program" />


                        <p>
                            eToro is pleased to introduce the <b>eToro Shareholder Reward Program</b>, an exclusive reward program
                            created to thank our community of ETOR shareholders and elevate the ownership experience.
                        </p>


                        <h3>What is the eToro Shareholder Reward Program?</h3>

                        <p>
                        As an ETOR shareholder, you’re now eligible to unlock curated rewards and experiences through our
                        partnership with <b>Stockperks</b>, a digital rewards platform built for equity holders. The more ETOR shares
                        you hold, the greater the rewards you can access.
                        </p>


                        <h3>How it Works</h3>
                        <p>The program is <b>open to all investors holding ETOR shares</b>, including those held on eToro and external brokerage accounts. To participate and claim your rewards:</p>

                        <ul>
                            <li>Download the Stockperks app on iOS or Android</li>
                            <li>Create a Stockperks profile (if you don’t already have one)</li>
                            <li>Locate the eToro company page within Stockperks</li>
                            <li>Connect your shareholding by following the prompts to validate your ETOR shares</li>
                            <li>Once validated, you’ll be able to see and claim the rewards corresponding to your share tier</li>
                        </ul>

                        <h3>Reward Tiers & Rewards</h3>
                        <p>
                            ETOR shareholders can access a variety of perks that scale with share ownership, from digital collectibles and premium content to exclusive experiences and advanced platform tools.
                        </p>

                        <TableScroll>
                        <PerksTable aria-label="eToro Reward Tiers">
                            <thead>
                            <tr>
                                <TableHeader></TableHeader>
                                {perksMatrix.columns.map((c) => (
                                    <TableHeader key={c.key}>{c.label}</TableHeader>
                                ))}
                            </tr>
                            </thead>

                            <tbody>
                            {perksMatrix.rows.map((row) => (
                                <tr key={row.label}>
                                    <TableCellStrong>{row.label}</TableCellStrong>

                                    {perksMatrix.columns.map((c) => {
                                        const value = row.values[c.key]

                                        return (
                                            <TableCellCenter key={`${row.label}-${c.key}`}>
                                                {row.type === "check" ? <CheckIcon $isOn={Boolean(value)} /> : <span>{String(value)}</span>}
                                            </TableCellCenter>
                                        )
                                    })}
                                </tr>
                            ))}
                            </tbody>
                        </PerksTable>
                        </TableScroll>



                        <h3>Who Can Participate?</h3>
                        <p>This program is available to anyone who owns ETOR shares, whether you hold them on the eToro platform or through another broker. The more shares you hold, the more rewards you can unlock.</p>

                        <h4>Important Notes</h4>
                            <ul>
                                <li>Rewards are <b>non-transferable</b> and linked to verified ETOR share ownership.</li>
                                <li>Some rewards may have specific claim windows or additional terms. Please review the details within Stockperks.</li>
                                <li>eToro reserves the right to modify or discontinue the program or its rewards at any time.</li>
                                <li>
                                    For any questions, please contact: <a href={"mailto:etoro@stockperks.com"}>etoro@stockperks.com</a>
                                </li>
                            </ul>

                        <h2>Get Started</h2>
                        <p>
                            To explore your rewards and begin claiming today:
                        </p>
                        <p>Download <b>Stockperks on iOS / Android</b> and search for “eToro”
                        </p>

                        <div style={{ display: "flex", justifyContent: "center", marginBottom: 30 }}>
                            <AppStoreLinks />
                        </div>


                        <p>
                            <em>
                                Disclaimer: this shareholder benefits program is provided by eToro Group Ltd. (“eToro”) as a shareholder engagement
                                initiative. It does not constitute investment advice, a recommendation, or an inducement to buy, sell,
                                or hold eToro shares or any other financial instrument. Eligibility is based solely on objective share
                                ownership criteria. Participation is optional and does not affect the performance of your investments.
                                Any benefits offered under the program may be amended or withdrawn at any time.
                            </em>

                        </p>

                        <p>
                            For any questions, please contact:{" "}
                            <a href={"mailto:etoro@stockperks.com"}>etoro@stockperks.com</a>
                        </p>

                        <p>
                            The Stockperks service is governed by Stockperks’ Privacy Policy, Terms of Service, and may be subject
                            to other Stockperks policies and terms. For Stockperks support, account setup, and questions regarding
                            the use of Stockperks, please contact{" "}
                            <a href={"mailto:hello@stockperks.com"}>hello@stockperks.com</a>.
                        </p>


                        <h3>eToro Shareholder Reward Program FAQs</h3>
                        <FAQList>
                            {faqs.map((f) => (
                                <details key={f.q}>
                                    <summary>{f.q}</summary>
                                    <FAQAnswer>{f.a}</FAQAnswer>
                                </details>
                            ))}
                        </FAQList>
                    </Content>
                </Body>
            </PageContent>
        </>
    )
}

export default EtoroPage

const PageContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    background-color: white;
    padding-top: 95px;

    @media (max-width: ${windowSize.medium}px) {
        padding-top: 64px;
    }
`

const Header = styled.div`
  display: flex;
  height: 100px;
  padding: 20px;
  flex-direction: row;
  align-items: center;
`

const Divider = styled.div`
  height: 100%;
  border-left: 1px solid black;
  margin: 0 16px;
`

const BannerImage = styled.img`
  width: 100%;
    max-width: 500px;
  height: auto;
  margin-bottom: 20px;

  @media (max-width: ${windowSize.medium}px) {
    max-width: 100%;
  }
`

const TopLogo = styled.img`
    height: ${(p) => (p.$variant === "etoro" ? "40px" : "120px")};
    width: auto;

    @media (max-width: ${windowSize.medium}px) {
        height: ${(p) => (p.$variant === "etoro" ? "30px" : "80px")};
    }
`

const Body = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 1200px;
  width: 100%;
`

const Content = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 24px 32px;
    width: 100%;

    @media (max-width: ${windowSize.medium}px) {
        padding: 16px 20px;

        p {
            padding: 0 12px;
        }
    }

    
`

const Title = styled.h1`
  font-family: Arial, Helvetica, serif;
  font-size: 35px;
  font-weight: 700;
  color: black;
  text-align: center;
`

const TableScroll = styled.div`
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    
    @media (max-width: ${windowSize.medium}px) {
        padding: 0 15px;
    }
`

const PerksTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    margin: 8px 0 18px;
    border-top: 2px solid black;
    border-bottom: 2px solid black;

    th,
    td {
        border-bottom: 1px solid #cfcfcf;
        padding: 10px 12px;
        vertical-align: top;
        font-family: Arial, Helvetica, serif;
        font-size: 14px;
        color: black;
    }

    tbody tr:last-child td {
        border-bottom: none;
    }
    
    @media (max-width: ${windowSize.medium}px) {
        width: max-content;    
        min-width: 620px;    

        th,
        td {
            padding: 8px 10px;
            font-size: 12px;
            white-space: nowrap;
        }
    }
`

const TableHeader = styled.th`
  text-transform: uppercase;
  font-size: 12px;
  letter-spacing: 0.02em;
  text-align: center;
`

const TableCellCenter = styled.td`
    text-align: center;
`

const TableCellStrong = styled.td`
  font-weight: 700;
  text-align: left;
`

const CheckIcon = styled.span`
    display: inline-flex;
    width: 18px;
    height: 18px;
    border-radius: 4px;
    align-items: center;
    justify-content: center;

    background: ${(p) => (p.$isOn ? "#2e7d32" : "transparent")};
    border: 2px solid ${(p) => (p.$isOn ? "#2e7d32" : "#bdbdbd")};
    color: white;
    font-size: 12px;
    font-weight: 800;

    &::before {
        content: ${(p) => (p.$isOn ? '"✓"' : '""')};
        line-height: 1;
    }
`
/** FAQ */
const FAQList = styled.div`
    width: 100%;
    margin-top: 10px;

    details {
        border-bottom: 1px solid #cfcfcf;
        padding: 12px 0;
    }

    summary {
        cursor: pointer;
        font-family: Arial, Helvetica, serif;
        font-size: 16px;         
        letter-spacing: 0;     
        line-height: 1.5;   
        color: black;
        outline: none;
    }
    
    details[open] summary {
        margin-bottom: 10px;
    }

    @media (max-width: ${windowSize.medium}px) {
        details {
            padding: 15px 15px;
        }
        details[open] summary {
            margin-bottom: 12px;
        }
        summary {
            font-size: 15px;
        }
    }
`

const FAQAnswer = styled.p`
    margin: 15px 0 0;
    font-family: Arial, Helvetica, serif;
    font-size: 15px;      
    letter-spacing: 0;     
    line-height: 1.5;   
    white-space: pre-line;
    color: black;
`